import React, { useEffect, useState } from 'react';
import { useForm } from 'react-final-form';

import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import Text from '@swf/components/Inputs/Text';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import type { Currency } from '@swf/core/enums';
import usePrevious from '@swf/core/hooks/usePrevious';
import type { TableParams } from '@swf/generated/types/api/v0/SwHomeGames';
import { ServerMessageHandler } from '@swf/notification';
import {
  PKR_RESTRICTED_SYMBOLS,
  PKR_RESTRICTED_SYMBOLS_STRING,
  mustNotContainSymbols
} from '@swf/poker';

import Buttons from '../../Modal/Buttons/Buttons';
import VideoChatToggle from '../../Modal/VideoChatToggle/VideoChatToggle';
import useForceUpdate from '../../Modal/hooks/useForceUpdate';
import BuyIns from '../BuyIns/BuyIns';
import useGetCurrency from '../hooks/useGetCurrency';
import useGetGameOptions from '../hooks/useGetGameOptions';
import useGetGameType from '../hooks/useGetGameType';
import useGetPlayersPerTable from '../hooks/useGetPlayersPerTable';
import useGetStakes from '../hooks/useGetStakes';
import type { FormValues } from '../utils/common';
import { getMatchingGameObject } from '../utils/common';

import styles from './Container.scss';
import {
  TXTCLI_Currency,
  TXTCLI_Players_Per_Table,
  TXTCLI_Stakes,
  TXTMOB_Create,
  TXTSRV_Game,
  TXTWCL_Table_Name,
  TXTWCL_The_following_characters_are_i_ELL,
  TXTWCL_Type_of_Game
} from './CreateGameForm.messages.json';

interface Props {
  gameData: TableParams[];
  loading: boolean;
  error?: string | null;
}

const CreateGameForm = ({ gameData, loading, error }: Props) => {
  const { change, getState } = useForm<FormValues>();
  const { values, validating, valid } = getState();
  const { handType, potStruct, tableSize, currency, stake, videoChatEnabled } = values;

  const gameOptions = useGetGameOptions(gameData);
  const gameTypeOptions = useGetGameType(gameData, handType);
  const playersPerTableOptions = useGetPlayersPerTable(gameData, handType, potStruct);
  const currencyOptions = useGetCurrency(gameData, handType, potStruct, tableSize);
  const stakesOptions = useGetStakes(gameData, values);

  const [videoSupported, setVideoSupported] = useState(false);
  const { currency: prevCurrency } = usePrevious(values);

  const { forceFieldUpdate } = useForceUpdate({
    values,
    change
  });

  useEffect(() => {
    if (loading) return;
    if (!handType || !currency || !stake) return;

    const tableParams = getMatchingGameObject({ ...values }, gameData);

    if (!tableParams && !videoSupported) return;
    if (currency !== prevCurrency) return;

    if (videoSupported && !tableParams) {
      setVideoSupported(false);

      if (videoChatEnabled) {
        change('videoChatEnabled', false);
      }

      return;
    }

    if (!tableParams) return;

    const { videoChatSupported } = tableParams;

    if (videoSupported !== videoChatSupported) {
      setVideoSupported(!!videoChatSupported);
    }
  }, [
    change,
    gameData,
    loading,
    values,
    handType,
    currency,
    stake,
    videoChatEnabled,
    videoSupported,
    prevCurrency
  ]);

  useEffect(() => {
    forceFieldUpdate({ field: 'potStruct', options: gameTypeOptions });
  }, [forceFieldUpdate, gameTypeOptions]);

  useEffect(() => {
    forceFieldUpdate({ field: 'tableSize', options: playersPerTableOptions });
  }, [forceFieldUpdate, playersPerTableOptions]);

  useEffect(() => {
    forceFieldUpdate({ field: 'currency', options: currencyOptions });
  }, [forceFieldUpdate, currencyOptions]);

  useEffect(() => {
    forceFieldUpdate({ field: 'stake', options: stakesOptions });
  }, [forceFieldUpdate, stakesOptions]);

  return (
    <>
      <div className={styles.form}>
        <Text
          id="tableName"
          label={TXTWCL_Table_Name}
          className={styles.tableName}
          validators={[
            mustNotContainSymbols(PKR_RESTRICTED_SYMBOLS, {
              ...TXTWCL_The_following_characters_are_i_ELL,
              values: { 0: PKR_RESTRICTED_SYMBOLS_STRING }
            })
          ]}
        />
        <div>
          <StarsDropDown
            id="handType"
            label={TXTSRV_Game}
            options={gameOptions}
            defaultOption={gameOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        <div>
          <StarsDropDown
            id="potStruct"
            label={TXTWCL_Type_of_Game}
            options={gameTypeOptions}
            defaultOption={gameTypeOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        <VideoChatToggle
          className={styles.videoChatToggle}
          checked={videoChatEnabled}
          supported={videoSupported}
          onChange={() => change('videoChatEnabled', !videoChatEnabled)}
        />
        <div>
          <StarsDropDown
            id="tableSize"
            label={TXTCLI_Players_Per_Table}
            options={playersPerTableOptions}
            defaultOption={playersPerTableOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        <div>
          <StarsDropDown
            id="currency"
            label={TXTCLI_Currency}
            options={currencyOptions}
            defaultOption={currencyOptions[0]}
            disabled={loading}
            className={styles.dropdown}
          />
        </div>
        <div className={styles.buyIn}>
          <StarsDropDown
            id="stake"
            label={TXTCLI_Stakes}
            options={stakesOptions}
            defaultOption={stakesOptions[0]}
            disabled={loading}
            className={styles.dropdown}
            caption={<BuyIns buyIns={values.stake} currency={values.currency as Currency} />}
          />
        </div>
        {error && (
          <StarsInfo className={styles.errorPanel} type="error">
            <ServerMessageHandler message={error} />
          </StarsInfo>
        )}
      </div>
      <Buttons
        className={styles.submitGroup}
        disabled={loading || !valid || validating}
        translation={TXTMOB_Create}
      />
    </>
  );
};

export default CreateGameForm;
